// @flow

import React, { PureComponent, type Node } from "react";
import { noop, isFunction, get } from "lodash";
import type { NullaryFn } from "@tvg/types/Functional";
import {
  cross,
  arrowLeft,
  successOutline,
  fanduel
} from "@fdr/static-ui/Icons/icons";
import Icon from "@fdr/static-ui/Icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import { FeedbackMessageContext } from "@fdr/utils/contextUtils";
import Button from "../../_atoms/Button";
import {
  NavigationTitleContainer,
  StatusIcon,
  Subtitle,
  Title,
  TitleContainer,
  TitleHolder,
  WidgetContainer,
  CloseButtonContainer,
  CloseLabel,
  NumberBadge
} from "./styled-components";
import { FixedBanner } from "../../_molecules/MessageV2";
import type { TitleAlignment } from "../../_templates/ModalV2/types";

export type TitleType =
  | "success"
  | "softSuccess"
  | "error"
  | "navigation"
  | "whiteNavigation"
  | "default"
  | "defaultBold"
  | "login"
  | "dark";

type Props = {
  /**
   * Title for the modal
   */
  title: string | Node | null,
  /**
   * Title alignment
   */
  titleAlign: TitleAlignment,
  /**
   * Title for the modal
   */
  subtitle: string | Node | null,
  /**
   * Type of title for the modal
   */
  titleType: TitleType,
  /**
   * Show close button inside whiteNavigation
   */
  hasClose: boolean,
  /**
   * Use the close label instead of close icon
   */
  useCloseLabel?: boolean,
  /**
   * Close label (only applicable if useCloseLabel is true)
   */
  closeLabel?: string,
  /**
   * CallBack for close the modal
   */
  onClose: NullaryFn<mixed>,
  /**
   * CallBack for close the modal
   */
  onBack: ?NullaryFn<mixed>,
  /**
   * should have round corners
   */
  hasRoundedCorners: boolean,
  /**
   * Header widget
   */
  widget: Node | null,
  /**
   * Sets the header sticky on top
   */
  isSticky: boolean,
  /**
   * Add a close button with no header
   */
  hasCloseWithoutHeader: boolean,
  /**
   * Check if the header should render the dropdown shadow
   */
  hasShadow: boolean,
  isCapitalized: boolean,
  isSubTitleUppercase: boolean,
  icon: ?Node,

  /**
   * Number badge to show up next to the title (i.e. my bets on a race)
   */
  titleBadgeNumber?: number,
  /**
   * Should the badge show if the number is 0
   */
  displayZeroTitleBadge?: boolean,
  /**
   * Check if the modal has full screen height
   */
  isFullHeight?: boolean,
  /**
   * Hide fixed banner on modal
   */
  hideFixedBanner?: boolean
};

export default class ModalHeader extends PureComponent<Props> {
  static contextType = FeedbackMessageContext;

  static defaultProps = {
    title: null,
    titleAlign: "left",
    subtitle: null,
    titleType: "default",
    hasClose: true,
    useCloseLabel: false,
    closeLabel: "Close",
    onClose: noop,
    onBack: null,
    hasRoundedCorners: true,
    widget: null,
    isSticky: false,
    hasCloseWithoutHeader: false,
    hasShadow: true,
    isCapitalized: true,
    isSubTitleUppercase: true,
    icon: null,
    titleBadgeNumber: undefined,
    displayZeroTitleBadge: false,
    isFullHeight: false,
    hideFixedBanner: false
  };

  render() {
    const bannerProps = get(this, "context.bannerProps");

    let ModalTitle;

    const genQALabelTitle = `modal-title-${
      typeof this.props.title === "string"
        ? String((this.props.title || "").toLowerCase().replace(/ /g, "-"))
        : "element"
    }`;

    switch (this.props.titleType) {
      case "success":
        ModalTitle = (
          <Title
            data-qa-label={`${genQALabelTitle}-${this.props.titleType}`}
            isCapitalized={this.props.isCapitalized}
            titleType={this.props.titleType}
          >
            <StatusIcon
              icon={successOutline}
              color={buildColor("white", "100")}
              size={20}
              qaLabel="modal-title-successOutline-icon"
            />
            {this.props.title}
          </Title>
        );
        break;
      case "login":
        ModalTitle = (
          <Title
            data-qa-label={`${genQALabelTitle}-${this.props.titleType}`}
            isCapitalized={this.props.isCapitalized}
            titleType={this.props.titleType}
          >
            {this.props.icon ? (
              this.props.icon
            ) : (
              <StatusIcon
                icon={this.props.icon || fanduel}
                color={buildColor("blue", "500")}
                size={20}
                qaLabel="modal-title-login-icon"
              />
            )}
            {this.props.title}
          </Title>
        );
        break;
      default:
        ModalTitle = (
          <Title
            data-qa-label={`${genQALabelTitle}-${this.props.titleType}`}
            isCapitalized={this.props.isCapitalized}
            titleType={this.props.titleType}
            hasIcon={!!this.props.icon}
            titleAlign={this.props.titleAlign}
          >
            {this.props.icon}
            {this.props.title}
            {this.props.titleBadgeNumber !== 0 &&
              this.props.displayZeroTitleBadge && (
                <NumberBadge>{this.props.titleBadgeNumber}</NumberBadge>
              )}
          </Title>
        );
    }

    if (this.props.subtitle) {
      ModalTitle = (
        <TitleHolder>
          {ModalTitle}
          <Subtitle
            title={this.props.title}
            useCloseLabel={this.props.useCloseLabel}
          >
            {this.props.subtitle}
          </Subtitle>
        </TitleHolder>
      );
    }

    const hasBack =
      /default|defaultBold|whiteNavigation/.test(this.props.titleType) &&
      isFunction(this.props.onBack);

    if (this.props.titleType === "navigation") {
      return (
        <TitleContainer
          data-qa-label="modal-header"
          titleType={this.props.titleType}
          isNavigation={this.props.titleType === "navigation"}
          roundedCorners={this.props.hasRoundedCorners}
          isSticky={this.props.isSticky}
          hasShadow={this.props.hasShadow}
          hasSubtitle={!!this.props.subtitle}
        >
          <NavigationTitleContainer>
            <Button qaLabel="modal-closeBtn" onClick={this.props.onClose}>
              <Icon
                icon={arrowLeft}
                color={buildColor("white", "100")}
                size={24}
                qaLabel="modal-closeBtn-icon"
              />
            </Button>
          </NavigationTitleContainer>
          <WidgetContainer>
            {ModalTitle}
            {this.props.widget}
          </WidgetContainer>
        </TitleContainer>
      );
    }
    if (this.props.titleType === "whiteNavigation") {
      return (
        <TitleContainer
          data-qa-label="modal-header"
          titleType={this.props.titleType}
          roundedCorners={this.props.hasRoundedCorners}
          isSticky={this.props.isSticky}
          hasBack={hasBack}
          hasShadow={this.props.hasShadow}
          hasSubtitle={!!this.props.subtitle}
        >
          {hasBack && (
            <NavigationTitleContainer hasPaddingLeft>
              <Button
                qaLabel="modal-backBtn"
                onClick={
                  // $FlowFixMe
                  isFunction(this.props.onBack) ? this.props.onBack : noop
                }
              >
                <Icon
                  icon={arrowLeft}
                  color={buildColor("navy", "900")}
                  size={24}
                  qaLabel="modal-backBtn-icon"
                />
              </Button>
            </NavigationTitleContainer>
          )}
          {ModalTitle}
          {this.props.hasClose && (
            <CloseButtonContainer>
              <Button qaLabel="modal-closeBtn" onClick={this.props.onClose}>
                <Icon
                  icon={cross}
                  color={buildColor("blue", "500")}
                  size={24}
                  qaLabel="modal-backBtn-icon"
                />
              </Button>
            </CloseButtonContainer>
          )}
        </TitleContainer>
      );
    }

    return (
      <React.Fragment>
        <TitleContainer
          data-qa-label="modal-header"
          titleType={this.props.titleType}
          roundedCorners={this.props.hasRoundedCorners}
          isSticky={this.props.isSticky}
          hasBack={hasBack}
          hasShadow={this.props.hasShadow}
          hasSubtitle={!!this.props.subtitle}
        >
          {hasBack && (
            <NavigationTitleContainer>
              <Button
                qaLabel="modal-backBtn"
                onClick={
                  // $FlowFixMe
                  isFunction(this.props.onBack) ? this.props.onBack : noop
                }
              >
                <Icon
                  icon={arrowLeft}
                  color={
                    this.props.titleType === "defaultBold"
                      ? buildColor("white", "100")
                      : buildColor("blue", "D1")
                  }
                  size={24}
                  qaLabel="modal-backBtn-icon"
                />
              </Button>
            </NavigationTitleContainer>
          )}
          {ModalTitle}
          {this.props.hasClose && (
            <CloseButtonContainer
              titleType={this.props.titleType}
              titleAlign={this.props.titleAlign}
            >
              <Button qaLabel="modal-closeBtn" onClick={this.props.onClose}>
                {this.props.useCloseLabel ? (
                  <CloseLabel titleType={this.props.titleType}>
                    {this.props.closeLabel}
                  </CloseLabel>
                ) : (
                  <Icon
                    icon={cross}
                    color={
                      this.props.titleType === "defaultBold" ||
                      this.props.titleType === "success"
                        ? buildColor("white", "100")
                        : buildColor("blue", "D1")
                    }
                    size={24}
                    qaLabel="modal-backBtn-icon"
                  />
                )}
              </Button>
            </CloseButtonContainer>
          )}
        </TitleContainer>
        {this.props.isFullHeight &&
          !this.props.hideFixedBanner &&
          bannerProps && <FixedBanner {...bannerProps} />}
      </React.Fragment>
    );
  }
}
